import { Moment } from 'moment'
import React, { FunctionComponent, useEffect, useState } from 'react'

import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../App/Hooks/reduxHooks'
import { activityListSelector } from '../../../../../App/store/features/activity/activitySlice'
import { Person } from '../../../../../App/store/features/person/IPerson'
import { Spinner } from '../../../../../Common/Spinner'
import { addAppointmentActivity } from '../../../../store/thunks/addAppointmentActivity'
import { deleteAppointmentActivity } from '../../../../store/thunks/deleteAppointmentActivity'
import { fetchAppointment } from '../../../../store/thunks/fetchAppointment'
import {
  IUpdateAppointmentData,
  updateAppointmentActivity,
} from '../../../../store/thunks/updateAppointmentActivity'
import { IAppointment, IAppointmentActivity } from '../../../../types'
import { isInstanceOfIThunkError } from '../../../../utils/interfaceCheck'
import { ICrudActivityDetails } from '../../../types/ICrudActivityDetails'
import { AddActivityViewSwitch } from '../../AddActivityViewSwitch'
import { IAddedActivity } from '../../AddActivityViewSwitch/IAddActivity'
import { ActionButtons } from '../ActionButtons'
import { PaymentStatusMessage } from '../PaymentStatusMessage'
import { UpdateRepeatMessage } from '../UpdateRepeatMessage'
import { checkIfMultiPersonActivity } from '../utils/checkIfMultiPersonActivity/checkIfMultiPersonActivity'
import { getActivitiesFromAppointment } from '../utils/getActivitiesFromAppointment/getActivitiesFromAppointment'

import { ConfirmationEmail } from './ConfirmationEmail'
import { UpdateFormFields } from './UpdateFormFields'

interface IProps {
  appointmentDetails: IAppointment
  personList: Person[]
}

export const UpdateDetails: FunctionComponent<IProps> = ({
  appointmentDetails,
  personList,
}) => {
  const dispatch = useAppDispatch()
  const [selectViewOpen, setSelectViewOpen] = useState<
    'activity' | 'menu' | undefined
  >(undefined)
  const [selectedActivitiesArray, setSelectedActivitiesArray] =
    useState<ICrudActivityDetails>({ data: [], actionType: 'init' })
  const [appointmentMoment] = useState<Moment>(appointmentDetails.startsAt)

  const { isOnlineBooked, appointmentId } = appointmentDetails
  const activityList = useAppSelector(activityListSelector)

  useEffect(() => {
    const appointmentActivities: IAppointmentActivity[] =
      getActivitiesFromAppointment(appointmentDetails, activityList || [])
    const reduceAppointmentActivities: IAddedActivity[] =
      checkIfMultiPersonActivity(
        appointmentActivities,
        activityList || [],
        personList,
      )
    setSelectedActivitiesArray({
      data: [...reduceAppointmentActivities],
      actionType: 'init',
    })
  }, [activityList, appointmentDetails, personList])

  useEffect(() => {
    const { actionType, activityUuid } = selectedActivitiesArray
    if (actionType === 'add') {
      // TODO add repeat popup if repeat
      const addedActivityIndex: number = selectedActivitiesArray.data.length - 1
      const newActivity: IAddedActivity =
        selectedActivitiesArray.data[addedActivityIndex]
      dispatch(
        addAppointmentActivity({
          personUuids: newActivity.personUuids,
          activityUuid: newActivity.activity.activityUuid,
          appointmentUuid: appointmentDetails.uuid,
          updateRepeatAppointments: false,
          isLocked: false,
        }),
      ).then(({ payload }) => {
        if (
          typeof payload !== 'undefined' &&
          !isInstanceOfIThunkError(payload)
        ) {
          dispatch(
            fetchAppointment({ uuid: payload.appointmentUuid, type: 'update' }),
          )
          return null
        }
        return null
      })
    }

    if (actionType === 'update' && typeof activityUuid !== 'undefined') {
      const selectedActivity = selectedActivitiesArray.data.find(
        activity => activity.appointmentActivityUuid === activityUuid,
      )

      if (
        !selectedActivity ||
        selectedActivity.appointmentActivityId === null
      ) {
        console.log('Should not be undefined OR NULL')
        return
      }
      // PERSON for activity can be wrong when more persons in activity
      // TODO loook at code in ActivityPersons.tsx -> editActivityPerson -> removes all for 1
      const personForActivity = personList.find(
        person => person.personUuid === selectedActivity.personUuids[0],
      )
      const updatedAppointmentActivity: IUpdateAppointmentData = {
        id: selectedActivity.appointmentActivityId,
        isLocked: selectedActivity.isLocked,
        startsAt: appointmentDetails.startsAt,
        endsAt: appointmentDetails.endsAt,
        personId: personForActivity ? personForActivity.id : undefined,
      }
      dispatch(
        updateAppointmentActivity({
          data: updatedAppointmentActivity,
          appointmentUuid: appointmentDetails.uuid,
          isLockedUpdate: selectedActivitiesArray.isLockedUpdate,
        }),
      ).then(({ payload }) => {
        if (
          typeof payload !== 'undefined' &&
          !isInstanceOfIThunkError(payload)
        ) {
          dispatch(
            fetchAppointment({ uuid: payload.appointmentUuid, type: 'update' }),
          )
          return null
        }
        return null
      })
    }

    if (actionType === 'delete' && activityUuid) {
      dispatch(
        deleteAppointmentActivity({
          activityUuid,
          appointmentUuid: appointmentDetails.uuid,
        }),
      ).then(({ payload }) => {
        if (
          typeof payload !== 'undefined' &&
          !isInstanceOfIThunkError(payload)
        ) {
          dispatch(
            fetchAppointment({ uuid: payload.appointmentUuid, type: 'delete' }),
          )
          return null
        }
        return null
      })
      console.log(appointmentDetails.appointmentActivity)
      console.log(selectedActivitiesArray.data)
      console.log(selectedActivitiesArray)
    }
  }, [selectedActivitiesArray])

  if (
    selectedActivitiesArray.data.length === 0 ||
    typeof activityList === 'undefined'
  ) {
    return <Spinner />
  }

  return (
    <>
      <AddActivityViewSwitch
        selectViewOpen={selectViewOpen}
        setSelectViewOpen={setSelectViewOpen}
        selectedActivityArray={selectedActivitiesArray.data}
        setSelectedActivityArray={setSelectedActivitiesArray}
        defaultPersonUuids={selectedActivitiesArray.data[0].personUuids}
        activityList={activityList}
        personList={personList}
      />
      <PaymentStatusMessage paymentStatus={appointmentDetails.paymentStatus} />
      <UpdateRepeatMessage repeatDetails={appointmentDetails.repeatDetails} />
      <UpdateFormFields
        appointment={appointmentDetails}
        isSelectViewOpen={!!selectViewOpen}
        appointmentMoment={appointmentMoment}
        setSelectedActivitiesArray={setSelectedActivitiesArray}
        selectedActivitiesArray={selectedActivitiesArray.data}
        setSelectViewOpen={setSelectViewOpen}
        customerDetails={appointmentDetails.customer}
      />
      <ActionButtons appointmentDetails={appointmentDetails} />
      <ConfirmationEmail
        isOnlineBooked={isOnlineBooked}
        appointmentId={appointmentId}
      />
    </>
  )
}
